import { Suspense, lazy, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Table } from "shared/Components";
import type { TableColumnType } from "shared/Components/Table/Components/SimpleTable";
import type { ShortPaymentTransactionModel } from "shared/types";
import { Button, Text } from "shared/uikit";
import { ButtonVariantsEnum, TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";

import { formatMoney } from "utils/formatMoney";

import { getDateTime } from "utils/getDateTime";

const PaymentDetailsModal = lazy(() => import("shared/Components/PaymentDetailsModal"));

interface TransactionsHistoryProps {
	transactions: ShortPaymentTransactionModel[];
	loading: boolean;
	loadingMore: boolean;
	onLoadMore?: () => void;
	exploreAllLink?: string;
	pageSize?: number;
}

const TransactionsHistory: React.FC<TransactionsHistoryProps> = ({
	transactions,
	loading,
	loadingMore,
	onLoadMore,
	exploreAllLink,
	pageSize
}) => {
	const { t } = useTranslation();

	const [showDetailsModal, setShowDetailsModal] = useState<{
		open: boolean;
		id: string;
	}>();

	const tableColumns: TableColumnType[] = useMemo(
		() => [
			{
				label: t("date"),
				width: 180,
				Cell: ({ rowData: { dateAt } }: { rowData: ShortPaymentTransactionModel }) => (
					<Text variants={TextVariantsEnum.BodyMedium}>{getDateTime(dateAt).toFormat("M/dd/yy")}</Text>
				),
				dataKey: "date"
			},
			{
				label: t("payment:order_number"),
				minWidth: 320,
				Cell: ({ rowData: { id } }: { rowData: ShortPaymentTransactionModel }) => (
					<Text variants={TextVariantsEnum.BodyMedium}>{id}</Text>
				),
				dataKey: "orderNumber"
			},
			{
				label: t("payment:price"),
				width: 120,
				Cell: ({ rowData: { amount, payoutId } }: { rowData: ShortPaymentTransactionModel }) => {
					const isCredit = !!payoutId;
					return amount ? (
						<Text variants={TextVariantsEnum.H7} className={isCredit ? "text-red-400" : "text-primary-600"}>
							{formatMoney(amount)}
						</Text>
					) : (
						"-"
					);
				},
				dataKey: "price"
			},
			{
				label: t("type"),
				width: 120,
				Cell: ({ rowData: { payoutId } }: { rowData: ShortPaymentTransactionModel }) => {
					const isCredit = !!payoutId;
					return isCredit ? (
						<Text variants={TextVariantsEnum.BodyMedium} className="text-red-400">
							{t("payment:credit")}
						</Text>
					) : (
						<Text variants={TextVariantsEnum.BodyMedium} className="text-primary-600">
							{t("payment:debit")}
						</Text>
					);
				},
				dataKey: "type"
			},
			{
				label: "",
				width: 180,
				Cell: ({ rowData: { id } }: { rowData: ShortPaymentTransactionModel }) => (
					<Button
						variant={ButtonVariantsEnum.GHOST}
						className="text-primary-600"
						onClick={() =>
							setShowDetailsModal({
								open: true,
								id
							})
						}
					>
						{t("view_details")}
					</Button>
				),
				dataKey: "status"
			}
		],
		[t]
	);

	return (
		<>
			<Table
				columns={tableColumns}
				data={transactions}
				loading={loading}
				loadingMore={loadingMore}
				label={
					<div className="flex items-center gap-2">
						<Text style={TextStylesEnum.Default} variants={TextVariantsEnum.H6} className="whitespace-nowrap">
							{t("payment:transactions_history")}
						</Text>
						{exploreAllLink && (
							<Button link={exploreAllLink} variant={ButtonVariantsEnum.LINK}>
								{t("explore_all")}
							</Button>
						)}
					</div>
				}
				tableSpacingClasses="px-4"
				rowHeight={80}
				pageSize={pageSize}
				onLoadMore={onLoadMore}
			/>
			{showDetailsModal?.open && (
				<Suspense>
					<PaymentDetailsModal id={showDetailsModal.id} onClose={() => setShowDetailsModal(undefined)} />
				</Suspense>
			)}
		</>
	);
};

export default TransactionsHistory;
